import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { API, useTheme } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Trophy,
  Target,
  TrendingUp,
  MessageSquare,
  ArrowRight,
  Lightbulb,
  ThumbsUp,
  AlertCircle,
  Mic,
  Sun,
  Moon,
  User
} from "lucide-react";

export default function SharedResults() {
  const { token } = useParams();
  const { theme, toggleTheme } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSharedSession = async () => {
      try {
        const response = await fetch(`${API}/shared/${token}`);
        if (response.ok) {
          const result = await response.json();
          setData(result);
        } else {
          setError("This share link is invalid or has expired.");
        }
      } catch (err) {
        console.error("Failed to fetch shared session:", err);
        setError("Failed to load shared results.");
      } finally {
        setLoading(false);
      }
    };
    fetchSharedSession();
  }, [token]);

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    return "text-red-500";
  };

  const getScoreBg = (score) => {
    if (score >= 80) return "bg-green-500";
    if (score >= 60) return "bg-yellow-500";
    return "bg-red-500";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md px-4">
          <AlertCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold mb-2">Link Not Found</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Link to="/">
            <Button>Go to Pitch Perfect</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { session, scenario, shared_by } = data;
  const { feedback, score, messages, scenario_name } = session;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center">
              <Mic className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg">Pitch Perfect</h1>
              <p className="text-xs text-muted-foreground">Shared Results</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Link to="/">
              <Button variant="outline" size="sm">Sign In</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        {/* Shared by banner */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <User className="w-4 h-4" />
          <span>Shared by {shared_by}</span>
        </div>

        {/* Title */}
        <div>
          <Badge variant="secondary" className="mb-2">Session Complete</Badge>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">{scenario_name}</h1>
        </div>

        {/* Score Overview */}
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="md:col-span-1 relative overflow-hidden">
            <div className={`absolute top-0 left-0 right-0 h-1 ${getScoreBg(score)}`} />
            <CardContent className="p-6 text-center">
              <Trophy className={`w-12 h-12 mx-auto mb-4 ${getScoreColor(score)}`} />
              <p className={`font-heading text-6xl font-bold ${getScoreColor(score)}`}>
                {score}
              </p>
              <p className="text-muted-foreground mt-2">Overall Score</p>
              <Badge 
                className={`mt-4 ${
                  score >= 80 ? "bg-green-500/10 text-green-500" :
                  score >= 60 ? "bg-yellow-500/10 text-yellow-500" :
                  "bg-red-500/10 text-red-500"
                }`}
              >
                {score >= 80 ? "Excellent" : score >= 60 ? "Good" : "Needs Work"}
              </Badge>
            </CardContent>
          </Card>

          <Card className="md:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Performance Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                {feedback?.summary}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Metrics */}
        {feedback?.metrics && Object.keys(feedback.metrics).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Skill Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {Object.entries(feedback.metrics).map(([key, value]) => (
                  <div key={key} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium capitalize">
                        {key.replace(/_/g, " ")}
                      </span>
                      <span className={`font-mono font-bold ${getScoreColor(value)}`}>
                        {value}%
                      </span>
                    </div>
                    <Progress value={value} className="h-2" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Strengths & Improvements */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-green-500">
                <ThumbsUp className="w-5 h-5" />
                Strengths
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {feedback?.strengths?.map((s, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-green-500 mt-2 flex-shrink-0" />
                    <span className="text-sm">{s}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-yellow-500">
                <Lightbulb className="w-5 h-5" />
                Areas for Improvement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {feedback?.areas_for_improvement?.map((a, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-yellow-500 mt-2 flex-shrink-0" />
                    <span className="text-sm">{a}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Next Steps */}
        {feedback?.next_steps?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ArrowRight className="w-5 h-5 text-primary" />
                Recommended Next Steps
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {feedback.next_steps.map((step, i) => (
                  <li key={i} className="flex items-center gap-3 p-3 rounded-lg bg-primary/5">
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <span className="text-sm">{step}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Transcript */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Conversation Transcript
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px]">
              <div className="space-y-4">
                {messages?.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        msg.role === "user"
                          ? "bg-primary text-primary-foreground rounded-br-sm"
                          : "bg-muted text-foreground rounded-bl-sm"
                      }`}
                    >
                      <p className="text-xs font-medium mb-1 opacity-70">
                        {msg.role === "user" ? "Salesperson" : "AI Client"}
                      </p>
                      <p className="text-sm">{msg.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* CTA */}
        <Card className="bg-gradient-primary text-white border-0">
          <CardContent className="p-6 text-center">
            <h3 className="font-heading text-xl font-bold mb-2">Try Pitch Perfect</h3>
            <p className="text-white/80 mb-4">
              Practice your sales skills with AI-powered voice roleplay.
            </p>
            <Link to="/">
              <Button variant="secondary">Get Started Free</Button>
            </Link>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
